import { useCallback } from "react"
import { useMutation } from "urql"

import type { DenyPriceChangeFailure, DenyPriceChangeInput, DenyPriceChangeMutation } from "@/graphql/codegen/graphql"
import { DenyPriceChangeDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseDenyPriceChangeSubmissionReturn {
  denyPriceChangeSubmission: (input: DenyPriceChangeInput) => Promise<boolean>
  isDenying: boolean
}

export const useDenyPriceChangeSubmission = (): UseDenyPriceChangeSubmissionReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, denyPriceChange] = useMutation<DenyPriceChangeMutation, { input: DenyPriceChangeInput }>(
    DenyPriceChangeDocument
  )

  const denyPriceChangeSubmission = useCallback(
    async (input: DenyPriceChangeInput): Promise<boolean> => {
      const { data, error } = await denyPriceChange({ input })
      const output = data?.priceChange.denyPriceChange
      const hasError = error || output?.__typename === "DenyPriceChangeFailure"

      if (hasError) {
        const message = error?.message ?? (output as DenyPriceChangeFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }
      notify({ type: NotificationType.Success, message: "Price changes denied" })
      return true
    },
    [denyPriceChange, notify]
  )

  return { denyPriceChangeSubmission, isDenying: fetching }
}
